import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Chip,
  LinearProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import styled from '@emotion/styled';
import moment from 'moment';

import { useSpot, RetailerContext } from 'framework';
import { Gap } from 'components';

import {
  BarChart,
  PieChart,
  LineChart,
  DataTable,
  NumberDisplay,
  TextDisplay,
  ProductListVisualization
} from './ai-visualization';

const QueryCard = styled(Box)`
  margin-bottom: 16px;
  width: 100%;
`;

const ResponseCard = styled(Box)``;

const QueryInput = styled(TextField)`
  width: 100%;
`;

const ResultBox = styled(Box)``;

const ExplanationBox = styled(Box)`
  font-style: italic;
  color: #555;
`;

const MetricCard = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 8px 0;
  min-height: 100px;
`;

const MetricValue = styled(Typography)<{ isPercentage?: boolean }>`
  font-size: ${props => (props.isPercentage ? '2rem' : '1.8rem')};
  font-weight: 700;
  color: #2c3e50;
  margin-top: 8px;
`;

const MetricLabel = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 500;
  color: #7f8c8d;
  text-align: center;
  text-transform: capitalize;
`;

const PercentageBadge = styled(Chip)<{ value: number }>`
  margin-left: 8px;
  background-color: ${props =>
    props.value > 0
      ? 'rgba(46, 204, 113, 0.15)'
      : props.value < 0
        ? 'rgba(231, 76, 60, 0.15)'
        : 'rgba(149, 165, 166, 0.15)'};
  color: ${props =>
    props.value > 0 ? '#27ae60' : props.value < 0 ? '#c0392b' : '#7f8c8d'};
  font-weight: 600;
`;

interface QueryRequest {
  query: string;
  retailer: string;
  startDate?: string;
  endDate?: string;
  dateRange?: string;
}

interface QueryJobResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  retailer: string;
  result?: AIQueryResult;
  error?: string;
}

interface AIQueryResult {
  data: {
    value: any;
    explanation: string;
  };
  metadata: {
    source: 'timestream' | 'projection';
    queryType: string;
    format: 'table' | 'bar' | 'line' | 'pie' | 'number' | 'text' | 'product';
    title: string;
    isComplete: boolean;
    query: string;
  };
}

interface MetricInfo {
  label: string;
  value: number | string;
  isPercentage: boolean;
  changeDirection?: number; // 1 for positive, -1 for negative, undefined for neutral
}

interface AIQueryProps {
  startDate?: moment.Moment;
  endDate?: moment.Moment;
  dateRange?: string;
}

export function AIQuery({ startDate, endDate, dateRange }: AIQueryProps) {
  const { t } = useTranslation();
  const { retailer } = useContext(RetailerContext);
  const { rawCommand, rawQuery, loading: apiLoading } = useSpot();
  const [query, setQuery] = useState<string>('');
  const [response, setResponse] = useState<AIQueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<
    'pending' | 'processing' | 'completed' | 'failed' | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Handle polling for job status
  useEffect(() => {
    if (!jobId || jobStatus === 'completed' || jobStatus === 'failed') {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Start polling if we have a jobId and it's not completed/failed
    if (!pollingRef.current) {
      pollingRef.current = setInterval(async () => {
        try {
          const jobResponse = (await rawQuery(
            `analytics/ai-query/${jobId}?retailer=${retailer?.slug}`,
            {}
          )) as QueryJobResponse;

          setJobStatus(jobResponse.status);

          if (jobResponse.status === 'completed' && jobResponse.result) {
            setResponse(jobResponse.result);
            setLoading(false);
            clearInterval(pollingRef.current!);
            pollingRef.current = null;
          } else if (jobResponse.status === 'failed') {
            setError(jobResponse.error || t('aiQueryError'));
            setLoading(false);
            clearInterval(pollingRef.current!);
            pollingRef.current = null;
          }
        } catch (err) {
          console.error('Error polling job status:', err);
          setError(t('aiQueryPollingError'));
          setLoading(false);
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
        }
      }, 2000); // Poll every 2 seconds
    }
  }, [jobId, jobStatus, rawCommand, t]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSubmit = async () => {
    if (!query.trim() || !retailer) return;

    setError(null);
    setResponse(null);
    setJobId(null);
    setJobStatus(null);
    setLoading(true);

    try {
      // Submit query to start processing
      const queryRequest: QueryRequest = {
        query: query.trim(),
        retailer: retailer.slug,
        // Include date parameters if available
        ...(startDate && { startDate: startDate.format('YYYY-MM-DD') }),
        ...(endDate && { endDate: endDate.format('YYYY-MM-DD') }),
        ...(dateRange && { dateRange })
      };

      const result = (await rawCommand(
        'analytics/ai-query',
        queryRequest as unknown as Record<string, unknown>
      )) as { id: string; status: 'pending' };

      // Set jobId and status to start polling
      setJobId(result.id);
      setJobStatus('pending');
    } catch (err) {
      console.error('Error submitting AI query:', err);
      setError(t('aiQuerySubmissionError'));
      setLoading(false);
    }
  };

  const isPercentage = (key: string, value: any): boolean => {
    // Check if the key suggests a percentage
    if (/percent|rate|ratio|proportion/i.test(key)) return true;

    // Check if the value is between 0 and 100 and has a decimal
    if (typeof value === 'number' && value >= 0 && value <= 100) {
      const stringVal = value.toString();
      return (
        stringVal.includes('.') || key.toLowerCase().includes('percentage')
      );
    }

    return false;
  };

  const formatMetricValue = (
    value: any,
    isPercentageValue: boolean
  ): string => {
    if (isPercentageValue) {
      return `${typeof value === 'number' ? value.toFixed(1) : value}%`;
    }

    if (typeof value === 'number') {
      // Format large numbers with commas
      return value.toLocaleString(undefined, {
        maximumFractionDigits: 2
      });
    }

    return String(value);
  };

  const extractMetrics = (value: any): MetricInfo[] => {
    // If it's a primitive value, return a single metric
    if (typeof value !== 'object' || value === null) {
      return [
        {
          label: t('aiQueryMetricValue'),
          value,
          isPercentage: typeof value === 'number' && value <= 100 && value >= 0
        }
      ];
    }

    // Handle array values
    if (Array.isArray(value)) {
      return value.map((item, index) => ({
        label: `${t('aiQueryMetric')} ${index + 1}`,
        value: item,
        isPercentage: false
      }));
    }

    // Process object with multiple values
    return Object.entries(value).map(([key, val]) => {
      const percentageValue = isPercentage(key, val);

      // Try to detect if this is a change or trending metric
      let changeDirection: number | undefined;
      const lowerKey = key.toLowerCase();
      if (
        lowerKey.includes('increase') ||
        lowerKey.includes('growth') ||
        lowerKey.includes('up')
      ) {
        changeDirection = 1;
      } else if (
        lowerKey.includes('decrease') ||
        lowerKey.includes('drop') ||
        lowerKey.includes('down')
      ) {
        changeDirection = -1;
      }

      return {
        label: key
          .replace(/([A-Z])/g, ' $1')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, str => str.toUpperCase()),
        value: val as number | string,
        isPercentage: percentageValue,
        changeDirection
      };
    });
  };

  return (
    <QueryCard>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('aiQueryTitle')}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {t('aiQueryDescription')}
        </Typography>

        {!retailer ? (
          <Box
            sx={{
              p: 2,
              bgcolor: '#FFF4E5',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography color="warning.dark">
              {t('selectRetailerForAiQuery')}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <QueryInput
              label={t('askAiPlaceholder')}
              variant="outlined"
              value={query}
              onChange={handleQueryChange}
              onKeyDown={e => e.key === 'Enter' && !loading && handleSubmit()}
              disabled={loading}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || apiLoading || !query.trim()}
              endIcon={
                loading || apiLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <SendIcon />
                )
              }
            >
              {loading ? t('processing') : t('sendQuery')}
            </Button>
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        {loading && jobStatus && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {jobStatus === 'pending' && t('aiQueryPending')}
                {jobStatus === 'processing' && t('aiQueryProcessing')}
              </Typography>
            </Box>
            <LinearProgress
              variant="indeterminate"
              sx={{
                '& .MuiLinearProgress-bar': {
                  backgroundColor:
                    jobStatus === 'pending' ? '#f39c12' : '#3498db'
                }
              }}
            />
          </Box>
        )}

        <Gap size={4} />
        {response && (
          <ResponseCard>
            <Typography variant="h6" gutterBottom>
              {response.metadata.title || t('aiQueryResult')}
            </Typography>
            <Gap />
            {response.data.value && (
              <ResultBox>
                {!response.metadata.format && (
                  <Grid container spacing={2}>
                    {extractMetrics(response.data.value).map(
                      (metric, index) => (
                        <Grid
                          item
                          xs={12}
                          sm={
                            extractMetrics(response.data.value).length > 2
                              ? 6
                              : 12
                          }
                          md={
                            extractMetrics(response.data.value).length > 2
                              ? 4
                              : 6
                          }
                          key={index}
                        >
                          <MetricCard>
                            <MetricLabel variant="subtitle2">
                              {metric.label}
                            </MetricLabel>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <MetricValue
                                variant="h4"
                                isPercentage={metric.isPercentage}
                              >
                                {formatMetricValue(
                                  metric.value,
                                  metric.isPercentage
                                )}
                              </MetricValue>

                              {metric.changeDirection !== undefined && (
                                <PercentageBadge
                                  size="small"
                                  value={metric.changeDirection}
                                  icon={
                                    metric.changeDirection > 0 ? (
                                      <TrendingUpIcon fontSize="small" />
                                    ) : (
                                      <TrendingDownIcon fontSize="small" />
                                    )
                                  }
                                  label={metric.changeDirection > 0 ? '↑' : '↓'}
                                />
                              )}
                            </Box>
                          </MetricCard>
                        </Grid>
                      )
                    )}
                  </Grid>
                )}

                {response.metadata.format === 'bar' &&
                  Array.isArray(response.data.value) && (
                    <BarChart
                      data={
                        response.data.value as Array<{
                          label: string;
                          value: number;
                        }>
                      }
                    />
                  )}

                {response.metadata.format === 'pie' &&
                  Array.isArray(response.data.value) && (
                    <PieChart
                      data={
                        response.data.value as Array<{
                          label: string;
                          value: number;
                        }>
                      }
                    />
                  )}

                {response.metadata.format === 'line' &&
                  typeof response.data.value === 'object' &&
                  !Array.isArray(response.data.value) && (
                    <LineChart
                      data={
                        response.data.value as {
                          [series: string]: Array<{
                            x: string | number;
                            y: number;
                          }>;
                        }
                      }
                    />
                  )}

                {response.metadata.format === 'table' &&
                  Array.isArray(response.data.value) && (
                    <DataTable
                      data={
                        response.data.value as Array<{ [key: string]: any }>
                      }
                    />
                  )}

                {response.metadata.format === 'number' &&
                  (typeof response.data.value === 'number' ||
                    typeof response.data.value === 'string') && (
                    <NumberDisplay
                      value={response.data.value as number | string}
                    />
                  )}

                {response.metadata.format === 'text' &&
                  typeof response.data.value === 'string' && (
                    <TextDisplay text={response.data.value as string} />
                  )}

                {response.metadata.format === 'product' &&
                  Array.isArray(response.data.value) && (
                    <ProductListVisualization
                      data={
                        response.data.value as Array<{
                          productId: string;
                          value: number;
                        }>
                      }
                    />
                  )}
              </ResultBox>
            )}

            <Gap size={4} />
            <ExplanationBox>
              <Typography variant="body1">
                {response.data.explanation}
              </Typography>
              <Gap />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 2,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  borderTop: '1px dashed #ddd',
                  paddingTop: 1
                }}
              >
                {t('aiQueryDisclaimer')}
              </Typography>
            </ExplanationBox>
          </ResponseCard>
        )}
      </CardContent>
    </QueryCard>
  );
}

export default AIQuery;
